/**
 * ArchivedTripBanner — iter193.
 *
 * Sticky banner at the top of an ended trip group. Shows:
 *   • "Trip ended N days ago by [admin name]"
 *   • Open-balances warning chip when applicable
 *   • Countdown until auto-purge (X days remaining)
 *   • Download PDF button (deep-link to existing /trip-summary export)
 *   • Reopen button (admin-only)
 *
 * Renders nothing when the group isn't archived.
 */
import { useMemo, useState } from 'react';
import { Flag, FileText, RotateCcw, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import SettleUpPanel from './SettleUpPanel';

const DAY_MS = 24 * 60 * 60 * 1000;

function daysBetween(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / DAY_MS));
}

function daysSince(iso) {
  if (!iso) return null;
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return null;
  return Math.max(0, Math.floor((Date.now() - start) / DAY_MS));
}

export default function ArchivedTripBanner({ group, isAdmin, currentUserId, onReopened }) {
  const [reopening, setReopening] = useState(false);
  const archivedAt = group?.archived_at;
  const deletedAt = group?.deleted_at;
  const hasOpenBalances = !!group?.has_open_balances;
  const endedByName = group?.archive_recap?.ended_by_name || 'admin';

  const sinceDays = useMemo(() => daysSince(archivedAt), [archivedAt]);
  const purgeInDays = useMemo(() => daysBetween(deletedAt), [deletedAt]);

  if (!archivedAt) return null;

  const handleDownloadPdf = async () => {
    const url = `${api.defaults.baseURL}/groups/${group.id}/trip-summary/export.pdf`;
    try {
      // Open in a new tab — axios is used elsewhere but PDF needs the browser
      // to render the file. We carry the auth token as a query param? No —
      // the existing pattern (per trip_exports.py) uses Bearer header. Use
      // window.open via a download anchor with the token injected by api.js.
      const res = await api.get(`/groups/${group.id}/trip-summary/export.pdf`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(group.name || 'trip').replace(/\s+/g, '_')}_recap.pdf`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error('Could not download PDF — ' + (e?.response?.data?.detail || 'try again'));
    }
  };

  const handleReopen = async () => {
    if (reopening) return;
    setReopening(true);
    try {
      const res = await api.post(`/groups/${group.id}/reopen-trip`);
      toast.success(res.data?.message || 'Trip reopened');
      onReopened?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen trip');
    } finally {
      setReopening(false);
    }
  };

  return (
    <div
      className="w-full rounded-2xl border-2 border-rose-300 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 space-y-2.5"
      data-testid="archived-trip-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-rose-200 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 flex items-center justify-center shrink-0">
            <Flag className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-rose-900 dark:text-rose-100">
              Trip ended {sinceDays === 0 ? 'today' : sinceDays === 1 ? 'yesterday' : `${sinceDays} days ago`}
              <span className="font-normal opacity-70"> by {endedByName}</span>
            </p>
            {hasOpenBalances && (
              <p className="text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 mt-0.5" data-testid="archived-open-balances">
                <AlertTriangle className="w-3 h-3" /> Ended with open balances · Settlements stay open
              </p>
            )}
            {purgeInDays !== null && (
              <p className="text-xs text-rose-800/80 dark:text-rose-200/80 inline-flex items-center gap-1 mt-0.5" data-testid="archived-purge-countdown">
                <Clock className="w-3 h-3" />
                {purgeInDays === 0 ? 'Auto-deletes today' : `Auto-deletes in ${purgeInDays} day${purgeInDays === 1 ? '' : 's'}`}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleDownloadPdf}
          data-testid="archived-download-pdf"
          className="flex-1 rounded-xl border-2 border-rose-300 bg-white dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 active:scale-[0.99] transition px-3 py-2 text-[12px] font-bold flex items-center justify-center gap-1.5"
        >
          <FileText className="w-3.5 h-3.5" />
          Download PDF
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={handleReopen}
            disabled={reopening}
            data-testid="archived-reopen-btn"
            className="flex-1 rounded-xl border-2 border-rose-300 bg-white dark:bg-rose-950/40 hover:bg-rose-100 dark:hover:bg-rose-900/40 active:scale-[0.99] transition px-3 py-2 text-[12px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {reopening ? 'Reopening…' : 'Reopen trip'}
          </button>
        )}
      </div>

      {/* iter194 — Settle Up CTA panel: per-pair Send / Nudge / Mark Received. */}
      {currentUserId && (
        <SettleUpPanel group={group} currentUserId={currentUserId} />
      )}
    </div>
  );
}
